'use client';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Loader2, Shield } from 'lucide-react';
import { QuoteFormData } from './QuickQuoteWizard';

interface Step3Props {
  form: UseFormReturn<QuoteFormData>;
  onBack: () => void;
  onSubmit: (data: QuoteFormData) => void;
  isSubmitting: boolean;
}

export default function Step3ContactInfo({ form, onBack, onSubmit, isSubmitting }: Step3Props) {
  const { register, handleSubmit, formState: { errors } } = form;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <div>
        <h2 className="text-2xl font-800 text-white mb-1">Almost there</h2>
        <p className="text-[#888] text-sm">Where should we send your free estimate?</p>
      </div>

      {/* Name row */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="leverage-label">First Name *</label>
          <input
            {...register('firstName', { required: 'Required' })}
            placeholder="Marcus"
            className="leverage-input"
            autoComplete="given-name"
          />
          {errors.firstName && <p className="text-red-400 text-xs mt-1">{errors.firstName.message}</p>}
        </div>
        <div>
          <label className="leverage-label">Last Name *</label>
          <input
            {...register('lastName', { required: 'Required' })}
            placeholder="Rivera"
            className="leverage-input"
            autoComplete="family-name"
          />
          {errors.lastName && <p className="text-red-400 text-xs mt-1">{errors.lastName.message}</p>}
        </div>
      </div>

      {/* Email */}
      <div>
        <label className="leverage-label">Email Address *</label>
        <input
          {...register('email', {
            required: 'Email is required',
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
          })}
          type="email"
          placeholder="marcus@example.com"
          className="leverage-input"
          autoComplete="email"
        />
        {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email.message}</p>}
      </div>

      {/* Phone */}
      <div>
        <label className="leverage-label">Phone Number *</label>
        <input
          {...register('phone', {
            required: 'Phone is required',
            pattern: { value: /^[\d\s\-\(\)\+]{10,}$/, message: 'Enter a valid phone number' },
          })}
          type="tel"
          placeholder="(555) 234-5678"
          className="leverage-input"
          autoComplete="tel"
        />
        {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone.message}</p>}
        <p className="text-[11px] text-[#555] mt-1">We&apos;ll text you when your estimate is ready</p>
      </div>

      {/* Address */}
      <div>
        <label className="leverage-label">Project Address *</label>
        <input
          {...register('address', { required: 'Address is required' })}
          placeholder="123 Oak Street, Austin, TX 78701"
          className="leverage-input"
          autoComplete="street-address"
        />
        {errors.address && <p className="text-red-400 text-xs mt-1">{errors.address.message}</p>}
      </div>

      {/* Notes */}
      <div>
        <label className="leverage-label">Additional Notes <span className="text-[#444] normal-case">(optional)</span></label>
        <textarea
          {...register('notes')}
          placeholder="Any specific details, access instructions, or questions..."
          rows={3}
          className="leverage-input resize-none"
        />
      </div>

      {/* Privacy note */}
      <div className="flex items-start gap-2.5 p-3 bg-[#0D0D0D] rounded-lg border border-[#1F1F1F]">
        <Shield size={15} className="text-blue-400 mt-0.5 flex-shrink-0" />
        <p className="text-[11px] text-[#666] leading-relaxed">
          Your information is encrypted and never sold. We use it only to deliver your estimate and follow up on your project.
        </p>
      </div>

      <div className="flex gap-3">
        <button type="button" onClick={onBack} disabled={isSubmitting} className="leverage-btn-secondary flex-shrink-0 px-5 py-4">
          ← Back
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="leverage-btn-primary flex-1 py-4 text-base flex items-center justify-center gap-2 min-w-0"
          style={{ minWidth: '160px' }}
        >
          {isSubmitting ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              <span>Calculating...</span>
            </>
          ) : (
            'Get My Estimate →'
          )}
        </button>
      </div>
    </form>
  );
}